import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { FileRecord } from "@/shared/domain/files";
import { fileQueryKeys } from "@/shared/domain/files/hooks";
import type { JointBinding } from "@/shared/state/visualization/schema/v1/three-d";

import type { SetJointBindingsResponse } from "../UrdfService";

export interface SetJointBindingsVariables {
  /**
   * The source `.urdf`. The route is addressed by it and derives the generated
   * asset the bindings are written into — the asset is never named directly.
   */
  file: Pick<FileRecord, "file_id" | "org_id">;
  /** The complete set, keyed by joint name. An empty record clears every binding. */
  bindings: Record<string, JointBinding>;
}

/**
 * Saves a robot's default joint bindings into its generated glTF.
 *
 * Resolves as soon as the platform has **queued** the write, not when the
 * artifact carries it: the declarations live in the asset's `ROBOTO_device`
 * extension, and rewriting it is a worker's job that takes seconds. Nothing here
 * waits on that and nothing polls for it — a second channel chasing the window
 * would report a staleness the design deliberately accepts. The editor already
 * knows what it sent, and says so.
 *
 * The consequence worth stating: a save is durable only once the worker runs.
 * There is no synchronous metadata write backing it up any more.
 *
 * The source file's record is invalidated because the platform stamps the queued
 * request onto it, and a re-open reads that record.
 */
export function useSetJointBindings() {
  const { urdf } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    SetJointBindingsResponse,
    Error,
    SetJointBindingsVariables
  >({
    mutationFn: ({ file, bindings }) =>
      urdf.setJointBindings(file.file_id, bindings, {
        resourceOwnerId: file.org_id,
      }),
    onSuccess: (_response, { file }) => {
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.detail(file.file_id),
        exact: true,
      });
    },
  });
}
